import { useLocalization } from "@fluent/react";
import { Button, Row } from "@narmi/design_system";
import { useEffect } from "react";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { PageContent } from "@lending/components/PageContent";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { useCurrentApplicationUuid } from "@lending/hooks/useCurrentApplicationUuid";
import { useFlowNavigation } from "@lending/navigation";
import { useAutoLoanFlowState } from "@lending/navigation/hooks/useAutoLoanFlowState";
import { useSelectedCarContent } from "./hooks/useSelectedCarContent";
import { RefinanceLoanTypeSelector } from "./RefinanceLoanTypeSelector";
import styles from "./SelectedCar.module.css";
import { VehicleStatusCards } from "./VehicleStatusCards";
import { useStore } from "../../../contexts/StoreContext";

function SelectedCar() {
  const { l10n } = useLocalization();

  const { handleVehicleSelection, hasRefinanceProducts, isApplicationLoading, isLoading } =
    useSelectedCarContent();
  const applicationUuid = useCurrentApplicationUuid();
  const { goBack, goNext } = useFlowNavigation();
  const { data: storeData, updateData } = useStore();
  const autoLoanFlowState = useAutoLoanFlowState();
  const isRefinanceSelected = storeData.isRefinanceSelected;
  const hasVehicleSelected = storeData.hasVehicleSelected;
  const isRefinance = hasRefinanceProducts ? isRefinanceSelected === true : false;
  const effectiveHasVehicleSelected = isRefinance ? true : hasVehicleSelected;
  const isAutoRequestedAmountEnabled = autoLoanFlowState.isAutoRequestedAmountEnabled;
  const shouldShowVehicleStatusCards = !hasRefinanceProducts || isRefinanceSelected === false;
  const isNextDisabled = hasRefinanceProducts
    ? isRefinanceSelected === undefined || effectiveHasVehicleSelected === undefined
    : effectiveHasVehicleSelected === undefined;
  const heading = hasRefinanceProducts
    ? l10n.getString("auto-loan-selected-car-heading", null, "What brings you here today?")
    : l10n.getString(
        "auto-loan-selected-car-no-refinance-heading",
        null,
        "Have you picked out your car?"
      );
  const subheading = hasRefinanceProducts
    ? l10n.getString(
        "auto-loan-selected-car-subheading",
        null,
        "Either way, you’ll get pre-qualified today — with no impact to your credit score."
      )
    : l10n.getString(
        "auto-loan-selected-car-no-refinance-subheading",
        null,
        "Either way, you’ll get pre-qualified — with no impact to your credit score."
      );
  const readyTitle = hasRefinanceProducts
    ? l10n.getString("auto-loan-selected-car-ready-title", null, "I've picked out my car")
    : l10n.getString(
        "auto-loan-selected-car-no-refinance-ready-title",
        null,
        "Yes, I’m ready to go"
      );
  const readyDescription = hasRefinanceProducts
    ? l10n.getString(
        "auto-loan-selected-car-ready-description",
        null,
        "Share your car details for a pre-qualified offer tailored to your exact purchase."
      )
    : l10n.getString(
        "auto-loan-selected-car-no-refinance-ready-description",
        null,
        "Share your car details for a pre-qualified offer tailored to your purchase."
      );
  const lookingTitle = hasRefinanceProducts
    ? l10n.getString("auto-loan-selected-car-looking-title", null, "I’m still shopping around")
    : l10n.getString(
        "auto-loan-selected-car-no-refinance-looking-title",
        null,
        "I’m still shopping around"
      );
  const lookingDescription = hasRefinanceProducts
    ? l10n.getString(
        "auto-loan-selected-car-looking-description",
        null,
        "Get a pre-qualified estimate — so you know your budget."
      )
    : l10n.getString(
        "auto-loan-selected-car-no-refinance-looking-description",
        null,
        "Get a pre-qualified estimate — so you know your budget."
      );

  useEffect(() => {
    if (!hasRefinanceProducts && isRefinanceSelected !== undefined) {
      updateData({ isRefinanceSelected: undefined });
    }
  }, [hasRefinanceProducts, isRefinanceSelected, updateData]);

  useEffect(() => {
    if (storeData.lendingApplicationID && storeData.lendingApplicationID !== applicationUuid) {
      updateData({
        isRefinanceSelected: undefined,
        hasVehicleSelected: undefined,
      });
    }
  }, [applicationUuid, storeData.lendingApplicationID, updateData]);

  function onClickReadyToGo() {
    updateData({ hasVehicleSelected: true });
  }

  function onClickStillLooking() {
    updateData({ hasVehicleSelected: false });
  }

  function onClickPurchase() {
    updateData({ isRefinanceSelected: false, hasVehicleSelected: undefined });
  }

  function onClickRefinance() {
    updateData({ isRefinanceSelected: true, hasVehicleSelected: undefined });
  }

  async function onClickNext() {
    if (effectiveHasVehicleSelected === undefined) {
      return;
    }

    const wasVehicleSelectionSaved = await handleVehicleSelection(
      effectiveHasVehicleSelected,
      isRefinance
    );

    if (!wasVehicleSelectionSaved) {
      return;
    }

    goNext({
      hasVehicleSelected: effectiveHasVehicleSelected,
      isRefinance,
      isAutoRequestedAmountEnabled,
    });
  }

  return (
    <PageLayout isLoading={isApplicationLoading}>
      <PageContent>
        <div className={styles.content}>
          <div>
            <PageHeader heading={heading} subheading={subheading} />
          </div>
          {hasRefinanceProducts ? (
            <>
              <RefinanceLoanTypeSelector
                isRefinanceSelected={isRefinanceSelected}
                onClickPurchase={onClickPurchase}
                onClickRefinance={onClickRefinance}
              />
              {shouldShowVehicleStatusCards && (
                <VehicleStatusCards
                  hasVehicleSelected={hasVehicleSelected}
                  onClickReadyToGo={onClickReadyToGo}
                  onClickStillLooking={onClickStillLooking}
                  readyTitle={readyTitle}
                  readyDescription={readyDescription}
                  lookingTitle={lookingTitle}
                  lookingDescription={lookingDescription}
                  showSectionLabel
                />
              )}
            </>
          ) : (
            <VehicleStatusCards
              hasVehicleSelected={hasVehicleSelected}
              onClickReadyToGo={onClickReadyToGo}
              onClickStillLooking={onClickStillLooking}
              readyTitle={readyTitle}
              readyDescription={readyDescription}
              lookingTitle={lookingTitle}
              lookingDescription={lookingDescription}
              variant="noRefinance"
            />
          )}
          <div className="margin--top--xl">
            <Row alignItems="center" justifyContent="space-between">
              <BackButton onClick={goBack} />
              <Button
                label={l10n.getString("button-label-next", null, "Next")}
                onClick={onClickNext}
                disabled={isNextDisabled}
                isLoading={isLoading}
              />
            </Row>
          </div>
        </div>
      </PageContent>
    </PageLayout>
  );
}

export { SelectedCar };
